import { useEffect, useRef, useState } from 'react';

const etapas = [
  {
    numero: 1,
    icon: 'ri-mental-health-line',
    titulo: 'Exercício guiado de visualização',
    descricao:
      'Relaxe e permita-se entrar em um estado de profunda conexão interior através de uma meditação guiada.',
  },
  {
    numero: 2,
    icon: 'ri-user-star-line',
    titulo: 'Encontro com sua melhor versão futura',
    descricao:
      'Visualize e sinta a presença da pessoa que você deseja se tornar, em todos os detalhes.',
  },
  {
    numero: 3,
    icon: 'ri-lightbulb-flash-line',
    titulo: 'Reflexão sobre metas, sonhos e vida ideal',
    descricao:
      'Explore suas aspirações mais profundas e o que realmente importa para você.',
  },
  {
    numero: 4,
    icon: 'ri-quill-pen-line',
    titulo: 'Escrita da carta',
    descricao:
      'Escreva uma mensagem autêntica e poderosa para sua versão futura, com toda a emoção e clareza.',
  },
  {
    numero: 5,
    icon: 'ri-calendar-check-line',
    titulo: 'Escolha do prazo',
    descricao:
      'Decida quando deseja receber sua carta: em 1, 2, 3 ou 5 anos.',
  },
  {
    numero: 6,
    icon: 'ri-shield-keyhole-line',
    titulo: 'Armazenamento seguro',
    descricao:
      'Sua carta é guardada com total privacidade e segurança até a data escolhida.',
  },
  {
    numero: 7,
    icon: 'ri-mail-open-line',
    titulo: 'Entrega futura da carta',
    descricao:
      'No momento certo, você receberá sua carta e poderá refletir sobre sua jornada de transformação.',
  },
];

const ComoFuncionaMetodo = () => {
  const [visibleSteps, setVisibleSteps] = useState<Set<number>>(new Set());
  const [headerVisible, setHeaderVisible] = useState(false);
  const stepsRef = useRef<Map<number, HTMLDivElement>>(new Map());
  const headerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const idx = Number(entry.target.getAttribute('data-idx'));
            if (!Number.isNaN(idx)) {
              setVisibleSteps((prev) => {
                const next = new Set(prev);
                next.add(idx);
                return next;
              });
            }
            if (entry.target === headerRef.current) {
              setHeaderVisible(true);
            }
          }
        });
      },
      { threshold: 0.15 },
    );

    if (headerRef.current) observer.observe(headerRef.current);
    stepsRef.current.forEach((el) => {
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <section className="bg-luxury-black py-28 relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-gold-500/20 to-transparent" />
      <div className="absolute top-20 right-0 w-96 h-96 bg-gold-500/3 rounded-full blur-3xl" />
      <div className="absolute bottom-20 left-0 w-80 h-80 bg-gold-500/3 rounded-full blur-3xl" />

      <div className="container mx-auto px-6 lg:px-16 relative z-10">
        {/* Header */}
        <div ref={headerRef} className="text-center mb-20">
          <div
            className="flex items-center justify-center gap-4 mb-6 transition-all duration-700"
            style={{
              opacity: headerVisible ? 1 : 0,
              transform: headerVisible ? 'translateY(0)' : 'translateY(20px)',
            }}
          >
            <div
              className="h-px bg-gradient-to-r from-transparent to-gold-500/40 transition-all duration-700"
              style={{ width: headerVisible ? '4rem' : '0rem', transitionDelay: '300ms' }}
            />
            <span className="font-['Montserrat'] text-xs tracking-[0.3em] uppercase text-gold-500/60">
              O Método
            </span>
            <div
              className="h-px bg-gradient-to-l from-transparent to-gold-500/40 transition-all duration-700"
              style={{ width: headerVisible ? '4rem' : '0rem', transitionDelay: '300ms' }}
            />
          </div>
          <h2
            className="font-['Playfair_Display'] text-4xl lg:text-5xl text-white font-light mb-5 transition-all duration-800"
            style={{
              opacity: headerVisible ? 1 : 0,
              transform: headerVisible ? 'translateY(0)' : 'translateY(25px)',
              filter: headerVisible ? 'blur(0px)' : 'blur(3px)',
              transitionDelay: '200ms',
            }}
          >
            Como funciona o{' '}
            <span className="italic text-gold-400">Carta do Eu Futuro</span>
          </h2>
          <p
            className="font-['Montserrat'] text-sm text-white/40 max-w-2xl mx-auto leading-relaxed font-light tracking-wide transition-all duration-700"
            style={{
              opacity: headerVisible ? 1 : 0,
              transform: headerVisible ? 'translateY(0)' : 'translateY(15px)',
              transitionDelay: '400ms',
            }}
          >
            Um processo de 7 etapas cuidadosamente desenhado para guiar você em
            uma jornada profunda de autoconhecimento e transformação pessoal.
          </p>
        </div>

        {/* Steps grid */}
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-5">
            {etapas.map((etapa, index) => {
              const isVisible = visibleSteps.has(index);

              // Build the class string outside JSX to avoid template‑literal parsing issues
              const stepClass = [
                'group',
                'relative',
                'rounded-xl',
                'border',
                'border-gold-500/10',
                'bg-white/[0.02]',
                'backdrop-blur-sm',
                'p-7',
                'hover:border-gold-500/25',
                'hover:bg-white/[0.04]',
                'transition-all',
                'duration-700',
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8',
              ].join(' ');

              return (
                <div
                  key={index}
                  data-idx={index}
                  ref={(el) => {
                    if (el) stepsRef.current.set(index, el);
                  }}
                  className={stepClass}
                  style={{ transitionDelay: `${index * 100}ms` }}
                >
                  <div className="flex items-start gap-5">
                    {/* Number + icon */}
                    <div className="flex-shrink-0 relative">
                      <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-gold-500/15 to-gold-600/5 flex items-center justify-center group-hover:from-gold-500/25 group-hover:to-gold-600/10 transition-all duration-500">
                        <i
                          className={`${etapa.icon} text-2xl text-gold-500 group-hover:text-gold-400 transition-colors duration-300`}
                        />
                      </div>
                      <span className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-gradient-to-br from-gold-500 to-gold-700 flex items-center justify-center">
                        <span className="font-['Montserrat'] text-[10px] font-bold text-white">
                          {etapa.numero}
                        </span>
                      </span>
                    </div>

                    {/* Text */}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-['Playfair_Display'] text-lg text-white font-semibold mb-2 group-hover:text-gold-300 transition-colors duration-300">
                        {etapa.titulo}
                      </h3>
                      <p className="font-['Montserrat'] text-xs text-white/40 leading-relaxed font-light tracking-wide">
                        {etapa.descricao}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Bottom highlight */}
          <div className="mt-14 text-center">
            <div className="inline-flex items-center gap-3 bg-gradient-to-r from-gold-500/10 via-gold-500/5 to-gold-500/10 border border-gold-500/15 rounded-full px-8 py-4">
              <i className="ri-sparkling-2-line text-gold-500 text-lg w-5 h-5 flex items-center justify-center" />
              <span className="font-['Montserrat'] text-xs text-gold-400/80 tracking-wider font-light">
                Baseado em neurociência, PNL e técnicas comprovadas de
                visualização criativa
              </span>
              <i className="ri-sparkling-2-line text-gold-500 text-lg w-5 h-5 flex items-center justify-center" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ComoFuncionaMetodo;
